import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { selectIsPreviewActive } from "../features/rolePreview/rolePreviewSlice";
import { Icon } from "@iconify/react";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Loading from "../components/spinners/Loading";
import { setDialog } from "../features/dialog/dialogSlice";
import { useGetMemberSelfSiteQuery } from "../features/members/membersApiSlice";
import { useGetTenantPlanQuery } from "../features/tenants/tenantsApiSlice";
import {
  FEATURE_KEYS,
  PLAN_IDS,
  hasFeatureAccess,
  normalizePlanId,
} from "../utils/planFeatures";
import {
  getOpeningDayLabel,
  normalizeDayValues,
  sortOpeningHoursByDay,
  sortTrainingSessions,
} from "../utils/openingHours";

const WEEKDAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const normalizeExternalUrl = (url) => {
  const raw = String(url || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
};

const getSiteId = (site) => String(site?._id || "");

const buildAddressLines = (site) => {
  if (!site) return [];
  const explicitAddress = String(site?.publicAddress || "").trim();
  if (explicitAddress) {
    return explicitAddress
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
  }

  const firstLine = String(site?.address || site?.street || "").trim();
  const secondLine = [site?.zip, site?.city].filter(Boolean).join(" ").trim();
  return [firstLine, secondLine].filter(Boolean);
};

const buildOpeningHoursItems = (site, todayKey) => {
  const publicItems = Array.isArray(site?.publicOpeningHours)
    ? site.publicOpeningHours
        .map((entry) => String(entry || "").trim())
        .filter(Boolean)
    : [];

  if (publicItems.length) {
    return publicItems.map((label, index) => ({
      key: `public-${index}`,
      label,
      highlight: false,
    }));
  }

  return sortOpeningHoursByDay(site?.openingHours || [])
    .filter((entry) => entry?.day && entry?.start && entry?.end)
    .map((entry, index) => ({
      key: `${entry?.day || "unknown"}-${index}`,
      label: `${getOpeningDayLabel(entry?.day)}: ${entry?.start} - ${entry?.end}`,
      highlight: entry?.day === todayKey,
    }));
};

const buildTrainingItems = (site) => {
  const publicItems = Array.isArray(site?.publicTrainingTimes)
    ? site.publicTrainingTimes
        .map((entry) => String(entry || "").trim())
        .filter(Boolean)
    : [];

  if (publicItems.length) {
    return publicItems.map((entry, index) => ({
      key: `public-${index}`,
      title: entry,
      dayLabel: "",
      timeLabel: "",
    }));
  }

  return sortTrainingSessions(site?.trainingSessions || []).map((entry, index) => {
    const normalizedDays = normalizeDayValues(entry?.days ?? entry?.day);
    const dayLabel =
      normalizedDays.length > 0
        ? normalizedDays.map(getOpeningDayLabel).join(", ")
        : getOpeningDayLabel(entry?.day);

    const timeParts = [entry?.start, entry?.end].filter(Boolean);
    const timeLabel =
      timeParts.length === 2 ? `${timeParts[0]} - ${timeParts[1]}` : timeParts[0] || "";

    return {
      key: `${entry?.title || "training"}-${index}`,
      title: entry?.title || "Training",
      dayLabel,
      timeLabel,
    };
  });
};

const sortSitesForMember = (sites, ownSiteId) =>
  [...sites].sort((a, b) => {
    const aId = getSiteId(a);
    const bId = getSiteId(b);

    if (aId === ownSiteId && bId !== ownSiteId) return -1;
    if (bId === ownSiteId && aId !== ownSiteId) return 1;

    return String(a?.siteName || "").localeCompare(String(b?.siteName || ""), "de");
  });

export default function MemberSitePage() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isPreviewActive = useSelector(selectIsPreviewActive);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetMemberSelfSiteQuery();
  const { data: tenant } = useGetTenantPlanQuery();

  const ownSiteId = String(data?.memberSiteId || data?.site?._id || "");
  const planId = normalizePlanId(tenant?.billingPlan || PLAN_IDS.BASIS);
  const publicRegistrationEnabled = hasFeatureAccess(
    planId,
    FEATURE_KEYS.PUBLIC_REGISTRATION,
  );

  const sites = useMemo(() => {
    const rawSites = Array.isArray(data?.sites)
      ? data.sites
      : data?.site
        ? [data.site]
        : [];

    const uniqueById = new Map();
    rawSites.forEach((entry) => {
      const id = getSiteId(entry);
      if (!id) return;
      if (!uniqueById.has(id)) {
        uniqueById.set(id, entry);
      }
    });

    return sortSitesForMember([...uniqueById.values()], ownSiteId);
  }, [data?.site, data?.sites, ownSiteId]);

  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    if (!sites.length) {
      setSelectedSiteId("");
      return;
    }

    const selectedStillVisible = sites.some((entry) => getSiteId(entry) === selectedSiteId);
    if (selectedSiteId && selectedStillVisible) return;

    const fallbackId = ownSiteId || getSiteId(sites[0]);
    setSelectedSiteId(fallbackId);
  }, [ownSiteId, selectedSiteId, sites]);

  const selectedSite = useMemo(
    () => sites.find((entry) => getSiteId(entry) === selectedSiteId) || null,
    [selectedSiteId, sites],
  );

  const todayKey = useMemo(() => WEEKDAY_KEYS[new Date().getDay()], []);

  const buildRegistrationLink = useCallback(
    (siteId) => {
      if (!siteId || !publicRegistrationEnabled || typeof window === "undefined") {
        return "";
      }
      return `${window.location.origin}/registration/${siteId}`;
    },
    [publicRegistrationEnabled],
  );

  const selectedRegistrationLink = useMemo(
    () => buildRegistrationLink(getSiteId(selectedSite)),
    [buildRegistrationLink, selectedSite],
  );

  const selectedAddressLines = useMemo(
    () => buildAddressLines(selectedSite),
    [selectedSite],
  );
  const selectedOpeningHours = useMemo(
    () => buildOpeningHoursItems(selectedSite, todayKey),
    [selectedSite, todayKey],
  );
  const selectedTrainings = useMemo(
    () => buildTrainingItems(selectedSite),
    [selectedSite],
  );
  const selectedSiteInfoText = String(selectedSite?.publicInfoText || "").trim();
  const selectedHomepage = useMemo(
    () => normalizeExternalUrl(selectedSite?.homepage),
    [selectedSite?.homepage],
  );

  const openSiteDetails = (siteId) => {
    setSelectedSiteId(siteId);
    setDetailsOpen(true);
  };

  const handleOpenRegistration = useCallback(
    (siteId) => {
      const link = buildRegistrationLink(siteId);
      if (!link) return;
      window.open(link, "_blank", "noopener,noreferrer");
    },
    [buildRegistrationLink],
  );

  const handleCopyRegistrationLink = useCallback(
    async (siteId) => {
      const link = buildRegistrationLink(siteId);
      if (!link) return;

      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(link);
        } else {
          const input = document.createElement("textarea");
          input.value = link;
          input.style.position = "fixed";
          input.style.opacity = "0";
          document.body.appendChild(input);
          input.focus();
          input.select();
          document.execCommand("copy");
          document.body.removeChild(input);
        }

        dispatch(
          setDialog({
            type: "SUCCESS",
            title: "Link kopiert",
            data: {
              successText:
                "Der Link zur öffentlichen Anmeldung wurde in die Zwischenablage kopiert.",
            },
          }),
        );
      } catch (copyError) {
        dispatch(
          setDialog({
            type: "ERROR",
            title: "Kopieren fehlgeschlagen",
            data: {
              errorText:
                "Der Link konnte nicht kopiert werden. Bitte kopieren Sie ihn manuell.",
            },
          }),
        );
      }
    },
    [buildRegistrationLink, dispatch],
  );

  if (isLoading) return <Loading />;

  if (isPreviewActive && isError && !data) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Helmet>
          <title>MA | Standorte</title>
        </Helmet>
        <Alert severity="info" variant="outlined" sx={{ py: 2 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Keine Mitgliedsdaten vorhanden
          </Typography>
          <Typography variant="body2">
            Ihr Benutzerkonto ist nicht als Mitglied registriert. In der
            Rollen-Vorschau können Mitgliedsseiten daher nicht mit echten Daten
            angezeigt werden. Diese Ansicht würde für ein echtes Mitglied die
            Standortinformationen zeigen.
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Standorte | Mitgliederverwaltung</title>
      </Helmet>

      <Container maxWidth="lg" sx={{ pb: 4 }}>
        <Stack spacing={2.5}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              borderColor: alpha(theme.palette.primary.main, 0.3),
              background: `linear-gradient(130deg, ${alpha(
                theme.palette.info.main,
                0.2,
              )} 0%, ${alpha(theme.palette.primary.main, 0.08)} 55%, ${alpha(
                theme.palette.background.paper,
                0.95,
              )} 100%)`,
            }}
          >
            {isFetching && (
              <LinearProgress
                sx={{
                  height: 2,
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 0,
                  },
                }}
              />
            )}
            <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(theme.palette.primary.main, 0.14),
                    }}
                  >
                    <Icon
                      icon="fa-solid:torii-gate"
                      width={24}
                      style={{ color: theme.palette.primary.main }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ letterSpacing: 1.2 }}
                    >
                      Mitgliederbereich
                    </Typography>
                    <Typography variant="h4" sx={{ lineHeight: 1.2 }}>
                      Standorte
                    </Typography>
                  </Box>
                </Stack>

                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760 }}>
                  Hier sehen Sie alle für Mitglieder freigegebenen Standorte Ihres Vereins.
                  Ihr zugewiesener Standort steht automatisch an erster Stelle.
                </Typography>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={`${sites.length} Standort${sites.length === 1 ? "" : "e"}`}
                  />
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label="Mein Standort zuerst"
                  />
                </Stack>
              </Stack>
            </Box>
          </Paper>

          {isError && (
            <Alert severity="error">
              {error?.data?.message ||
                "Standortinformationen konnten nicht geladen werden."}
            </Alert>
          )}

          {!sites.length && !isError && (
            <Alert severity="warning">
              Für Ihr Profil sind aktuell keine sichtbaren Standorte hinterlegt.
            </Alert>
          )}

          {sites.length > 0 && (
            <Grid container spacing={2.5}>
              {sites.map((site) => {
                const siteId = getSiteId(site);
                const isOwnSite = siteId === ownSiteId;
                const addressLines = buildAddressLines(site);
                const openingCount = buildOpeningHoursItems(site, todayKey).length;
                const trainingCount = buildTrainingItems(site).length;

                return (
                  <Grid item xs={12} md={6} lg={4} key={siteId}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderColor: isOwnSite
                          ? alpha(theme.palette.primary.main, 0.45)
                          : "divider",
                        background: isOwnSite
                          ? `linear-gradient(160deg, ${alpha(
                              theme.palette.primary.main,
                              0.1,
                            )}, ${alpha(theme.palette.background.paper, 0.95)})`
                          : theme.palette.background.paper,
                        transition: "transform 180ms ease, box-shadow 180ms ease",
                        "&:hover": {
                          transform: "translateY(-3px)",
                          boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
                        },
                      }}
                    >
                      <CardActionArea onClick={() => openSiteDetails(siteId)}>
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              spacing={1}
                              alignItems="flex-start"
                            >
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Icon
                                  icon="fa-solid:torii-gate"
                                  width={18}
                                  style={{ color: theme.palette.primary.main }}
                                />
                                <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
                                  {site?.siteName || "Standort"}
                                </Typography>
                              </Stack>
                              {isOwnSite && (
                                <Chip size="small" color="primary" label="Mein Standort" />
                              )}
                            </Stack>

                            <Typography variant="body2" color="text.secondary">
                              {addressLines[0] || "Keine Anschrift hinterlegt"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {addressLines[1] || ""}
                            </Typography>

                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`${openingCount} Öffnungszeiten`}
                              />
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`${trainingCount} Trainings`}
                              />
                            </Stack>

                            <Stack direction="row" spacing={0.7} alignItems="center">
                              <Typography variant="button" color="primary.main">
                                Details ansehen
                              </Typography>
                              <Icon
                                icon="mdi:arrow-right"
                                width={18}
                                style={{ color: theme.palette.primary.main }}
                              />
                            </Stack>
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Stack>
      </Container>

      <Dialog
        open={detailsOpen && Boolean(selectedSite)}
        onClose={() => setDetailsOpen(false)}
        fullWidth
        maxWidth="lg"
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
            <Stack direction="row" spacing={1.1} alignItems="center">
              <Icon
                icon="fa-solid:torii-gate"
                width={20}
                style={{ color: theme.palette.primary.main }}
              />
              <Typography variant="h6">
                {selectedSite?.siteName || "Standortdetails"}
              </Typography>
            </Stack>
            {getSiteId(selectedSite) === ownSiteId && (
              <Chip size="small" color="primary" label="Mein Standort" />
            )}
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={7}>
              <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                    <Icon
                      icon="mdi:information-outline"
                      width={20}
                      style={{ color: theme.palette.primary.main }}
                    />
                    <Typography variant="h6">Über den Standort</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {selectedSiteInfoText ||
                      "Für diesen Standort ist aktuell kein zusätzlicher Informationstext hinterlegt."}
                  </Typography>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                    <Icon
                      icon="mdi:map-marker-outline"
                      width={20}
                      style={{ color: theme.palette.primary.main }}
                    />
                    <Typography variant="h6">Anschrift & Kontakt</Typography>
                  </Stack>

                  <Box sx={{ mb: 1.25 }}>
                    {selectedAddressLines.length > 0 ? (
                      selectedAddressLines.map((line, index) => (
                        <Typography key={`${line}-${index}`} variant="body2" color="text.secondary">
                          {line}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Keine Anschrift hinterlegt.
                      </Typography>
                    )}
                  </Box>

                  <Divider sx={{ mb: 1.25 }} />

                  <Stack spacing={1.1}>
                    {selectedSite?.mail && (
                      <Link
                        href={`mailto:${selectedSite.mail}`}
                        underline="hover"
                        color="inherit"
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Icon icon="mdi:email-outline" width={18} />
                        <Typography variant="body2">{selectedSite.mail}</Typography>
                      </Link>
                    )}

                    {selectedSite?.phone && (
                      <Link
                        href={`tel:${selectedSite.phone}`}
                        underline="hover"
                        color="inherit"
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Icon icon="mdi:phone-outline" width={18} />
                        <Typography variant="body2">{selectedSite.phone}</Typography>
                      </Link>
                    )}

                    {selectedHomepage && (
                      <Link
                        href={selectedHomepage}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        color="inherit"
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Icon icon="mdi:web" width={18} />
                        <Typography variant="body2">{selectedSite?.homepage}</Typography>
                      </Link>
                    )}

                    {!selectedSite?.mail && !selectedSite?.phone && !selectedHomepage && (
                      <Typography variant="body2" color="text.secondary">
                        Für diesen Standort sind keine Kontaktdaten hinterlegt.
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <Icon
                      icon="mdi:clock-time-four-outline"
                      width={20}
                      style={{ color: theme.palette.primary.main }}
                    />
                    <Typography variant="h6">Öffnungszeiten</Typography>
                  </Stack>

                  <Stack spacing={1}>
                    {selectedOpeningHours.length > 0 ? (
                      selectedOpeningHours.map((entry) => (
                        <Box
                          key={entry.key}
                          sx={{
                            p: 1.2,
                            borderRadius: 1.5,
                            border: "1px solid",
                            borderColor: entry.highlight ? "primary.main" : "divider",
                            bgcolor: entry.highlight
                              ? alpha(theme.palette.primary.main, 0.08)
                              : alpha(theme.palette.grey[500], 0.04),
                          }}
                        >
                          <Typography variant="body2" fontWeight={entry.highlight ? 700 : 500}>
                            {entry.label}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Es sind derzeit keine Öffnungszeiten hinterlegt.
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <Icon
                      icon="mdi:calendar-clock-outline"
                      width={20}
                      style={{ color: theme.palette.primary.main }}
                    />
                    <Typography variant="h6">Trainingsplan</Typography>
                  </Stack>

                  <Stack spacing={1.1}>
                    {selectedTrainings.length > 0 ? (
                      selectedTrainings.map((entry) => (
                        <Box
                          key={entry.key}
                          sx={{
                            p: 1.2,
                            borderRadius: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: alpha(theme.palette.grey[500], 0.04),
                          }}
                        >
                          <Typography variant="body2" fontWeight={700}>
                            {entry.title}
                          </Typography>
                          {(entry.dayLabel || entry.timeLabel) && (
                            <Typography variant="caption" color="text.secondary">
                              {[entry.dayLabel, entry.timeLabel].filter(Boolean).join(" · ")}
                            </Typography>
                          )}
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Für diesen Standort ist noch kein Trainingsplan hinterlegt.
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                  >
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Icon
                          icon="mdi:account-plus-outline"
                          width={20}
                          style={{ color: theme.palette.primary.main }}
                        />
                        <Typography variant="h6">Link zur öffentlichen Anmeldung</Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        Teilen Sie diesen Link mit Freunden oder Bekannten, damit sie sich direkt
                        online anmelden können.
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", md: "auto" } }}>
                      <Button
                        variant="contained"
                        onClick={() => handleOpenRegistration(getSiteId(selectedSite))}
                        disabled={!selectedRegistrationLink}
                        fullWidth
                      >
                        Anmeldeseite öffnen
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => handleCopyRegistrationLink(getSiteId(selectedSite))}
                        disabled={!selectedRegistrationLink}
                        fullWidth
                      >
                        Link kopieren
                      </Button>
                    </Stack>
                  </Stack>

                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.2,
                      borderRadius: 1.5,
                      border: "1px dashed",
                      borderColor: "divider",
                      bgcolor: alpha(theme.palette.grey[500], 0.04),
                    }}
                  >
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                      {selectedRegistrationLink ||
                        (publicRegistrationEnabled
                          ? "Der Link ist aktuell nicht verfügbar, da kein Standort hinterlegt ist."
                          : "Die öffentliche Anmeldung ist in Ihrem Tarif derzeit nicht freigeschaltet.")}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)} variant="contained">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
